import { useTitleStore } from 'src/stores/useTitleStore';
import * as React from 'react';
import { useIntl } from 'react-intl';
import { Button } from 'src/registry/ui/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { booleanBadgeOptions, ICON_MAP } from 'src/utils/constants';
import { useNavigate, useParams } from 'react-router-dom';
import { subscriptionItemsMessages } from 'src/features/subscriptionItems/utils/messages';
import { useGetSubscriptionItem } from 'src/features/subscriptionItems/api/useGetSubscriptionItem';
import { servicePlansMessages } from 'src/features/servicePlans/utils/messages';
import { servicePlanItemsMessages } from 'src/features/servicePlanItems/utils/messages';
import { Badge } from 'src/registry/ui/Badge';
import { useBillableMetricMap } from 'src/features/billableMetrics/hooks/useBillableMetricMap';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { SectionBlock, SectionBlockHeader, SectionBlockTitle } from 'src/registry/components/blocks/SectionBlock';
import DashboardGeneralBlock from 'src/features/billing/components/DashboardGeneralBlock';
import { commonMessages, generalMessages } from 'src/utils/messages';
import UpdateSubscriptionItemFormModal from 'src/features/subscriptionItems/components/forms/UpdateSubscriptionItemFormModal';
import DeleteSubscriptionItemModal from 'src/features/subscriptionItems/components/forms/DeleteSubscriptionItemModal';
import SubscriptionItemQuantityEventsTable from 'src/features/subscriptionItemQuantityEvents/components/tables/SubscriptionItemQuantityEventsTable';
import SubscriptionItemUsageRecordTable from 'src/features/subscriptionItemUsageRecords/components/tables/SubscriptionItemUsageRecordTable';
import useHasPermissions from 'src/utils/useHasPermissions';
import { PERMISSION } from 'src/features/auth/types';

const SubscriptionItemDashboard = () => {
    const { formatMessage } = useIntl();
    const navigate = useNavigate();
    const { subscriptionId = '', subscriptionItemId = '' } = useParams();

    const { setTitle, removeTitle } = useTitleStore();

    React.useEffect(() => {
        setTitle(formatMessage(subscriptionItemsMessages.subscriptionItemDashboard));
        return removeTitle;
    }, [formatMessage, removeTitle, setTitle]);

    const { data: subscriptionItem } = useGetSubscriptionItem({
        subscriptionId,
        itemId: subscriptionItemId,
        config: { enabled: !!subscriptionItemId || !!subscriptionId },
    });

    const permissionManageSubscriptions = useHasPermissions([PERMISSION.MANAGE_SUBSCRIPTIONS]);

    // `billable_metric` is a bare UUID on the wire - the name has to come from the lookup.
    const metricMap = useBillableMetricMap();
    const billableMetric = subscriptionItem?.service_plan_item?.billable_metric
        ? metricMap.get(subscriptionItem.service_plan_item.billable_metric)
        : undefined;

    const isActiveAsString = subscriptionItem?.is_active ? 'true' : 'false';
    const isRequiredAsString = subscriptionItem?.service_plan_item.is_required ? 'true' : 'false';
    const itemIsRequired = subscriptionItem?.service_plan_item.is_required;

    const dashboardBlockData = {
        section1: [
            {
                title: servicePlansMessages.code,
                value: subscriptionItem?.service_plan_item.code ?? '-',
            },
            {
                title: servicePlanItemsMessages.externalId,
                value: subscriptionItem?.external_item_id ?? '-',
            },
            {
                title: servicePlanItemsMessages.metric,
                value: billableMetric ? `${billableMetric.name} (${billableMetric.code})` : '-',
            },
            {
                title: servicePlansMessages.isActive,
                value: (
                    <Badge theme={booleanBadgeOptions[isActiveAsString].theme || 'gray'}>
                        <FontAwesomeIcon icon={booleanBadgeOptions[isActiveAsString].icon as IconProp} />
                        {formatMessage(booleanBadgeOptions[isActiveAsString].label)}
                    </Badge>
                ),
            },
            {
                title: servicePlanItemsMessages.isRequired,
                value: (
                    <Badge theme={booleanBadgeOptions[isRequiredAsString].theme || 'gray'}>
                        <FontAwesomeIcon icon={booleanBadgeOptions[isRequiredAsString].icon as IconProp} />
                        {formatMessage(booleanBadgeOptions[isRequiredAsString].label)}
                    </Badge>
                ),
            },
        ],
    };

    return (
        <>
            <Button
                asChild
                className="place-self-start p-0"
                variant="link"
                onClick={() => navigate(`/subscriptions/${subscriptionId}`)}
            >
                <div className="flex cursor-pointer">
                    <FontAwesomeIcon icon={ICON_MAP.BACK} className="opacity-50" />
                    {formatMessage(subscriptionItemsMessages.backToSubscriptionDashboard)}
                </div>
            </Button>

            <div className="mt-6 grid grid-cols-1 gap-8">
                <SectionBlock>
                    <SectionBlockHeader>
                        <SectionBlockTitle className="text-2xl whitespace-nowrap">
                            {subscriptionItem?.service_plan_item.name}
                        </SectionBlockTitle>

                        {permissionManageSubscriptions && subscriptionItem && !itemIsRequired && (
                            <div className="flex flex-row gap-2">
                                <UpdateSubscriptionItemFormModal
                                    selectedSubscriptionItem={subscriptionItem}
                                    semantics={{
                                        entryName: formatMessage(subscriptionItemsMessages.item).toLowerCase(),
                                    }}
                                >
                                    <Button size="mdIcon" variant="outline" className="cursor-pointer">
                                        <FontAwesomeIcon icon={ICON_MAP.EDIT} />
                                        <span className="hidden md:block">{formatMessage(commonMessages.edit)}</span>
                                    </Button>
                                </UpdateSubscriptionItemFormModal>
                                <DeleteSubscriptionItemModal
                                    selectedSubscriptionItem={subscriptionItem}
                                    semantics={{
                                        entryName: formatMessage(subscriptionItemsMessages.item).toLowerCase(),
                                    }}
                                >
                                    <Button size="mdIcon" variant="destructiveLight" className="cursor-pointer">
                                        <FontAwesomeIcon icon={ICON_MAP.DELETE} />
                                        <span className="hidden md:block">{formatMessage(generalMessages.delete)}</span>
                                    </Button>
                                </DeleteSubscriptionItemModal>
                            </div>
                        )}
                    </SectionBlockHeader>
                    <DashboardGeneralBlock dashboardData={dashboardBlockData} indexForLastSeparator={0} />
                </SectionBlock>
                <SubscriptionItemQuantityEventsTable />
                <SubscriptionItemUsageRecordTable />
            </div>
        </>
    );
};

export default SubscriptionItemDashboard;
